(() => {
  'use strict';

  const PARENT_SELECTOR = '[data-catalog-parent]';
  const RAIL_SELECTOR = '[data-catalog-rail]';
  const CARD_SELECTOR = '[data-catalog-card]';
  const REDUCED_MOTION = window.matchMedia('(prefers-reduced-motion: reduce)');

  const getCarouselMetrics = (rail) => {
    const cards = [...rail.querySelectorAll(CARD_SELECTOR)];
    const firstCard = cards[0];
    const styles = window.getComputedStyle(rail);
    const gap = Number.parseFloat(styles.columnGap || styles.gap) || 0;
    const cardWidth = firstCard?.getBoundingClientRect().width || rail.clientWidth;
    const cardStep = cardWidth + gap;
    const firstCardLeft = firstCard?.getBoundingClientRect().left || 0;
    const rowsPerColumn = Math.max(1, cards.filter((card) => (
      Math.abs(card.getBoundingClientRect().left - firstCardLeft) < 2
    )).length);
    const visibleColumns = Math.max(1, Math.floor((rail.clientWidth + gap + 2) / cardStep));
    const totalColumns = Math.max(1, Math.ceil(cards.length / rowsPerColumn));
    const pageStep = cardStep * visibleColumns;
    // Count the screens the rail can actually scroll through, so the total always
    // matches the number of next-arrow clicks (a short last screen still counts once).
    const maximumScroll = Math.max(0, rail.scrollWidth - rail.clientWidth);
    const reachablePages = maximumScroll > 2 ? 1 + Math.ceil((maximumScroll - 2) / pageStep) : 1;
    const totalPages = Math.max(1, Math.min(Math.ceil(totalColumns / visibleColumns), reachablePages));

    return {
      pageStep,
      totalPages,
    };
  };

  const updateControls = (parent, rail, previousButtons, nextButtons, positions) => {
    const maximumScroll = Math.max(0, rail.scrollWidth - rail.clientWidth);
    const hasOverflow = maximumScroll > 2;
    const { pageStep, totalPages } = getCarouselMetrics(rail);
    const atEnd = hasOverflow && rail.scrollLeft >= maximumScroll - 2;
    const current = atEnd
      ? totalPages
      : Math.min(totalPages, Math.max(1, Math.round(rail.scrollLeft / pageStep) + 1));

    parent.classList.toggle('is-static', !hasOverflow);
    parent.classList.toggle('is-at-end', !hasOverflow || rail.scrollLeft >= maximumScroll - 2);

    positions.forEach((position) => {
      const currentLabel = position.querySelector('[data-catalog-current]');
      if (currentLabel) currentLabel.textContent = String(current);
      const totalLabel = position.querySelector('[data-catalog-total]');
      if (totalLabel) totalLabel.textContent = String(totalPages);
    });

    previousButtons.forEach((button) => {
      button.disabled = !hasOverflow || rail.scrollLeft <= 2;
    });
    nextButtons.forEach((button) => {
      button.disabled = !hasOverflow || rail.scrollLeft >= maximumScroll - 2;
    });
  };

  const getScrollDistance = (rail) => {
    return getCarouselMetrics(rail).pageStep;
  };

  const initializeParent = (parent) => {
    if (parent.dataset.catalogReady === 'true') return;

    const rail = parent.querySelector(RAIL_SELECTOR);
    if (!rail) return;

    const previousButtons = [...parent.querySelectorAll('[data-catalog-prev]')];
    const nextButtons = [...parent.querySelectorAll('[data-catalog-next]')];
    const positions = [...parent.querySelectorAll('[data-catalog-position]')];
    const move = (direction) => {
      rail.scrollBy({
        left: getScrollDistance(rail) * direction,
        behavior: REDUCED_MOTION.matches ? 'auto' : 'smooth',
      });
    };
    const refresh = () => updateControls(parent, rail, previousButtons, nextButtons, positions);

    parent.dataset.catalogReady = 'true';

    previousButtons.forEach((button) => button.addEventListener('click', () => move(-1)));
    nextButtons.forEach((button) => button.addEventListener('click', () => move(1)));
    rail.addEventListener('scroll', refresh, { passive: true });
    rail.addEventListener('keydown', (event) => {
      if (event.key !== 'ArrowLeft' && event.key !== 'ArrowRight') return;

      event.preventDefault();
      move(event.key === 'ArrowLeft' ? -1 : 1);
    });

    if ('ResizeObserver' in window) {
      const observer = new ResizeObserver(refresh);
      observer.observe(rail);
    } else {
      window.addEventListener('resize', refresh, { passive: true });
    }

    refresh();
  };

  const initialize = (scope = document) => {
    scope.querySelectorAll(PARENT_SELECTOR).forEach(initializeParent);
  };

  initialize();
  document.addEventListener('shopify:section:load', (event) => initialize(event.target));
})();
